import PropTypes from 'prop-types'
import copy from 'copy-to-clipboard'
import { Chip } from '@mui/material'
import { Modal } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import React, { useEffect, useState } from 'react'
import { createNotification } from 'react-redux-notify'
import { getSiteLogo, getSiteName } from '../../../utils/brandingConfig'

import ModalPrimaryButton from '../../Buttons/ModalPrimaryButton'
import PaymentCancleConfirmationModal from './PaymentCancleConfirmationModal'
import { getErrorNotificationMessage, getSuccessNotificationMessage } from '../../helper/NotificationMessage'

import './styles/AddFund.css'
import CopyIcon from '../../../../src/images_new/icon/copy-icon-wallet.svg?react'


const QRCodeModal = ({ show, hide, data }) => {
  const dispatch = useDispatch()
  const [timeLeftInSeconds, setTimeLeftInSeconds] = useState(0)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  
  const logo = getSiteLogo()
  const siteName = getSiteName()
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(data?.hosted_url)}`

  const handleConfirmCancel = () => {
    setShowConfirmModal(false)
    hide()
  }

  const handleCancelClose = () => {
    setShowConfirmModal(false)
  }

  useEffect(() => {
    if (!show || !data?.expires_at) return

    const expiryTime = new Date(data.expires_at).getTime()
    const now = Date.now()
    
    // If already expired, just set time to 0 and return
    if (expiryTime <= now) {
      setTimeLeftInSeconds(0)
      return
    }

    // Set initial time
    setTimeLeftInSeconds(Math.floor((expiryTime - now) / 1000))

    const interval = setInterval(() => {
      const now = Date.now()
      const diffInSeconds = Math.floor((expiryTime - now) / 1000)

      if (diffInSeconds <= 0) {
        setTimeLeftInSeconds(0)
        dispatch(createNotification(getErrorNotificationMessage('Link Expired')))
        clearInterval(interval)
      } else {
        setTimeLeftInSeconds(diffInSeconds)
      }
    }, 100)

    return () => clearInterval(interval)
  }, [show, data])

  const formatTime = (seconds) => {
    const days = Math.floor(seconds / (60 * 60 * 24))
    const hours = Math.floor((seconds % (60 * 60 * 24)) / (60 * 60))
    const minutes = Math.floor((seconds % (60 * 60)) / 60)
    const secs = seconds % 60

    if (days > 0) {
      return `${days} day${days !== 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`
    } else if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`
    }
  }

  const handleCancelClick = () => {
    if (timeLeftInSeconds <= 0) {
      hide()
    } else {
      setShowConfirmModal(true)
    }
  }

  const handleCopyReferralUrl = () => {
    copy(data?.hosted_url)
    dispatch(
      createNotification(
        getSuccessNotificationMessage('Link copied successfully')
      )
    )
  }

  return (
    <>
      <Modal
        size='lg'
        centered
        show={show}
        className='modal-dialog-center sent-tip-modal createstorymodal'
      >
        <Modal.Body className='p-4'>
          <div className='d-flex align-items-center justify-content-center text-center border-bottom mb-2'>
            <div className='d-flex justify-content-center align-items-center mb-2 gap-2'>
              <img
                role={'button'}
                src={logo}
                className='img-fluid m-0 qr-logo'
                alt={siteName}
                id='stripper_logo'
                aria-label={`${siteName} logo`}
              />
              { data?.is_in_test_mode && 
              <div className='d-flex justify-content-end'>
                <Chip
                  label='Test Mode'
                  color='success'
                  size='medium'
                  sx={{
                    borderRadius: '6px',
                    fontWeight: '600'
                  }}
                />
              </div>
              }
            </div>
          </div>

          <div className='text-center mb-3'>
            <h5 style={{ fontWeight: 500 }}>Scan to Pay with Crypto</h5>
          </div>

          <div className='d-flex justify-content-center mb-3'>
            <img src={qrCodeUrl} alt='QR Code' width={250} height={250} />
          </div>

          <div className='text-center mb-2'>
            <div><strong>Amount: {data?.amount} USD</strong></div>
            <span className='text-muted'>Expires in: </span>
            <span className={`fw-bold ${timeLeftInSeconds > 0 ? '' : 'text-danger'} `}>{timeLeftInSeconds > 0 ? formatTime(timeLeftInSeconds) : 'Link Expired'}</span>
          </div>

          {timeLeftInSeconds > 0 && (
            <>
              <div className='d-flex align-items-center justify-content-between mt-2 mb-3 bg-dark bg-opacity-10 p-2 rounded'>
                <span className='text-truncate' style={{ maxWidth: '80%' }}>{data?.hosted_url}</span>
                <div className='cursor-pointer' role='button' onClick={handleCopyReferralUrl}><CopyIcon /></div>
              </div>

              <div className='text-center text-muted mb-3'>
                Use your crypto wallet to complete the payment by scanning the QR code.
              </div>
            </>
          )}

          <div className='d-flex justify-content-center'>
            <ModalPrimaryButton title='Close' className='w-100' onClick={handleCancelClick} />
          </div>
        </Modal.Body>
      </Modal>
      <PaymentCancleConfirmationModal
        show={showConfirmModal}
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelClose}
      />
    </>
  )
}

QRCodeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  hide: PropTypes.func.isRequired,
  data: PropTypes.shape({
    hosted_url: PropTypes.string.isRequired,
    expires_at: PropTypes.string.isRequired,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    is_in_test_mode: PropTypes.bool
  }).isRequired
}

export default QRCodeModal
